/**
 * WHAT THE WORKSPACE DOES WITH THE SHELL'S READING OF THE OPERATING SYSTEM.
 *
 * THE COMPLAINT THIS ANSWERS: "user mouse activity movement based available,
 * away or offline status as microsoft teams - ours is not accurate in those."
 * It was not. `useActivityMonitor` measured Away from four DOM events on THIS
 * window, so a person writing code, editing a spreadsheet or reading mail in
 * another application delivered zero input to us and aged to Away five minutes
 * in, with their hands on the keyboard. Teams asks the OS (GetLastInputInfo)
 * and therefore knows a human is present whatever has focus.
 *
 * FEED BOTH CLOCKS. THERE ARE TWO, THEY LIVE IN DIFFERENT MODULES, AND ONLY ONE
 * OF THEM IS EVER SEEN BY THE SERVER.
 *
 *   - `presenceStore`'s `lastInputAt` drives the LOCAL resolver: this user's
 *     own chip, own menu, own profile card and own tray icon.
 *   - `createRealtimeClient`'s `lastActivityAt` is carried on every `ping` as
 *     `idleMs` and is the only idle signal the gateway ever receives. The
 *     gateway reads it in `handlePing` and calls
 *     `applyActivity(conn, idle < conn.autoAwayMs)`, which is what sets or
 *     clears F_IDLE and therefore what every colleague sees.
 *
 * A previous round pinned the first and left the second, so the server's view
 * of the socket stayed idle while this tab was sure the user was here. Both are
 * written from one number, in one function, here.
 *
 * ═══ AND ONLY EVER A NUMBER THAT WAS MEASURED ═══
 *
 * The round that added the OS reading then broke the same invariant from the
 * other side, and this file is where it broke. Three things that are NOT
 * measurements — a page load, a reconnect, and a window becoming visible — sent
 * `activity('active')`, which stamped the transport's clock at NOW. Both clocks
 * only move forward, so a belief written into the measurement slot could not
 * afterwards be corrected by the OS. Lock your screen with the idle counter at
 * three seconds and 25 s later `ping.idleMs` said 25 000, the gateway said
 * `25_000 < 300_000`, and every colleague saw a green dot on a locked machine
 * for the next five minutes — while this tab's own chip correctly read Away.
 *
 * The separation this file now keeps, and the reason each part exists:
 *
 *   MEASURED  `report.at - report.idleMs`, one absolute instant, written into
 *             both clocks and nothing else written into them from here.
 *   STATED    `locked`. Locking is itself input, so the measurement says the
 *             user is present and is right and useless; the statement outranks
 *             it in both clocks (`setSystemLocked`, `setAbsent`) until the OS
 *             takes it back. `unknown` is not a statement and takes nothing back.
 *   CONCLUDED `presenceClaim()`. The one place a conclusion is drawn, from the
 *             evidence above, for the one place a frame is sent.
 *
 * NOTHING IN THIS FILE RUNS IN A BROWSER — except `presenceClaim`, which a
 * browser reaches with `windowVisible` pinned true and no lock, i.e. exactly
 * its DOM-measured idle time. There is no web API for system-wide idle
 * (`IdleDetector` is Chromium-only, permission-gated and absent from a plain
 * tab), so a browser keeps the DOM listeners unchanged and never receives one
 * of these reports. This is an enhancement for the shell, not a replacement for
 * the browser path.
 */
import { getRealtime } from '@/features/chat/api';
import type { DesktopSystemActivity } from '@/features/desktop/types';
import { presenceActions, selfHasEvidence, selfIsIdle } from './presenceStore';

/**
 * IS THE WORKSPACE WINDOW ACTUALLY ON SCREEN — a fact only the shell has.
 *
 * The shell disables `backgroundThrottling` on this window, which Electron's
 * `webPreferences` documentation notes "also affects the Page Visibility API".
 * `document.visibilityState` is therefore pinned at `visible` for the life of
 * the window: minimise the app and this document is never told, so it never
 * sends the gateway its `background` hint.
 *
 * THAT IS NOT A COSMETIC GAP. `background` is what moves a connection off the
 * 60 s foreground reap (`appSilentMs > HEARTBEAT_TIMEOUT_MS`) and onto the
 * 120 s transport grace, where application silence is worth Away and never
 * Offline (apps/gateway/src/core/heartbeat.ts). Without it the desktop client -
 * the one that is supposed to be better than a tab - sits permanently on the
 * strictest reap branch in the system, with a hidden browser tab getting the
 * gentler treatment. Any stall over a minute costs it the socket.
 *
 * Kept here rather than in the transport because it is the same report that
 * carries the idle time, and one report should not be split across two owners.
 */
let windowVisible = true;
/**
 * THE SCREEN IS LOCKED, as the OS stated it.
 *
 * Mirrored here as well as in `presenceStore` because it is an input to
 * `presenceClaim`, and because it must survive an `unknown` reading: see
 * `applySystemActivity`.
 */
let locked = false;
let lastState: DesktopSystemActivity['state'] | null = null;
const evidenceListeners = new Set<() => void>();

/**
 * DOES THIS DOCUMENT MEASURE ITS USER AT ALL?
 *
 * NOT EVERY DOCUMENT DOES, and the one that does not is on screen while the
 * person is demonstrably present. `AppProviders` mounts `RealtimeRuntime` in
 * every document, including a desktop CALL WINDOW; `AppOverlays` deliberately
 * mounts `PresenceRuntime` only in the workspace, because a person has one
 * presence and two idle monitors would disagree about it. So a call window has
 * no DOM monitor, no OS reports and no tick - `evidenceAt()` there is the moment
 * that window opened and nothing ever moves it, which reads as five minutes of
 * absence from somebody who is mid-sentence.
 *
 * A document that measures nothing may make no claim about a human - in EITHER
 * DIRECTION. It goes on reporting its WINDOW (`background`, a fact about a
 * window and true whoever is or is not in front of it) and leaves both `active`
 * and `idle` to the document that is actually watching. `framesFor` is where
 * that is enforced; a round ago it was only ASSERTED here, and the code did the
 * opposite - see the defect recorded there.
 *
 * "IN EITHER DIRECTION" WAS WRONG ABOUT SILENCE, and that error cost a whole
 * round. Saying nothing is not neutral, because `redis/presence.ts onConnect`
 * does `hincrby(k, "a", 1)` the moment a socket arrives: an unheard-from
 * connection is a connection voting AVAILABLE. And the call window was not even
 * silent - its 25 s heartbeat carried `now - openedAt` from the transport
 * clock's seed, which `dispatch.ts:209` reads as a measurement. Locking the
 * screen with a call window open showed AVAILABLE to every colleague for five
 * minutes, and again for 25 s after each reconnect, for ever.
 *
 * So a non-measuring document must RETRACT its arrival rather than abstain from
 * it, and it does that by never being granted the seed at all: see
 * `measurePresenceHere` below, and the invariant at the top of
 * `lib/realtime/idleClock.ts`, which is where it is enforced for every socket
 * on every path.
 *
 * IT HAS TO BE ENFORCED, BECAUSE THE GATEWAY AGGREGATES. `redis/presence.ts
 * setActivity` resolves `activeCount > 0 ? available : away` across ALL of a
 * user's sockets, so the most optimistic connection wins outright and no
 * amount of honesty on the workspace's socket can outvote one call window
 * claiming a keyboard.
 */
let measuring = false;

/**
 * `PresenceRuntime` declaring that it is here. Returns the undo.
 *
 * It re-reports on the way in, so this cannot depend on mount ORDER:
 * `RealtimeRuntime` is an earlier sibling, and its first assertion has already
 * gone out saying nothing about a human by the time this is called.
 *
 * THE RE-REPORT IS NOT FORCED, AND THAT IS DELIBERATE. It used to clear the
 * reporter's `lastClaim` as well, which is a second way of saying the same
 * thing: `measured` is part of the claim, so declaring CHANGES the claim and
 * `reportPresence` sends it for that reason alone. Having both meant neither
 * was load-bearing - deleting the `measured` comparison in `sameClaim` left
 * every suite in the repo green, because reaching around it into `lastClaim`
 * produced the same answer. Found by mutation, and the redundant half is the
 * one that reaches around.
 */
export function measurePresenceHere(): () => void {
  measuring = true;
  /*
   * AND THE SEED IS GRANTED HERE, TO BOTH CLOCKS, IN ONE GESTURE.
   *
   * "Somebody loaded this page, so somebody is probably at it" is a decent
   * guess in the document a HUMAN opened and a lie in one the SHELL opened. It
   * used to be a property of every clock ever constructed, which is how a call
   * window - which nobody navigated to, which measures nothing, and which can
   * therefore never take the guess back - spent five minutes an hour telling
   * the gateway that a locked workstation had somebody at it.
   *
   * The declaration and the entitlement are the same fact, so they are the same
   * call: a document that watches its user may guess about them, and one that
   * does not, may not. Both clocks are written together for the reason
   * `setSystemLocked`/`setAbsent` are - they are one piece of state about one
   * person held in two modules, and a round that granted one and not the other
   * would have the owner's chip and their colleagues' rosters disagreeing for
   * five minutes at a time.
   */
  presenceActions.assumeHumanHere();
  getRealtime().assumeOpenedByHuman();
  /* AND THE CHIP HEARS IT NOW, not at the next 15 s beat. A tick that already
     ran without the grant decided "nobody here" and nothing else would revisit
     that for a quarter of a minute - Away on every fresh page. */
  presenceActions.tick();
  for (const handler of evidenceListeners) handler();
  return () => {
    measuring = false;
  };
}

/** True when the shell has told us this window is minimised or in the tray. */
export function shellWindowIsHidden(): boolean {
  return !windowVisible;
}

/**
 * Everything the claim is drawn from, as one comparable value.
 *
 * The three facts a report can change that could change what this client may
 * tell the server. The idle TIME is deliberately absent: it changes on every
 * keepalive and crossing the away threshold is a matter of the clock rather
 * than of a report, which is what the 15 s beat in `useActivityMonitor` and the
 * 25 s `ping` are for.
 */
function evidence(): string {
  return `${String(windowVisible)}|${String(locked)}|${String(lastState)}`;
}

/**
 * Fires when the shell's evidence CHANGES.
 *
 * `RealtimeRuntime` owns the socket's lifecycle and re-asserts presence on
 * every reconnect, so it subscribes here rather than this module sending frames
 * of its own on a schedule: one owner for when a frame goes out, one owner for
 * what it says (`reportPresence`), and no chance of the two disagreeing about
 * whether this window is in the background.
 */
export function onShellEvidenceChange(handler: () => void): () => void {
  evidenceListeners.add(handler);
  return () => {
    evidenceListeners.delete(handler);
  };
}

/**
 * BACK TO KNOWING NOTHING.
 *
 * The window is assumed visible (a browser must never look backgrounded), the
 * lock is dropped from BOTH clocks, the transition history goes, and the store
 * forgets both idle measurements - one gesture over BOTH clocks, because they
 * are one piece of state about one person held in two modules, and a reset that
 * cleared half of it would leave the other half describing somebody who is no
 * longer there. Both fall back to the same document-load assumption.
 *
 * THAT ASSUMPTION IS NOT THE SAFE DIRECTION, and this block used to say it was.
 * It is older than any measurement, but in a document younger than the away
 * threshold it still reads Available - so a reset while the machine was LOCKED
 * (or idle) painted a green dot on an empty desk until the seed aged out, and
 * nothing corrected it: the shell pushes nothing while nobody is at the
 * machine. The lock and the OS reading describe the MACHINE, which did not
 * change when the person did, so `PresenceRuntime` re-reads them from the
 * shell (`pullSystemActivity`) straight after every reset it makes. A lock
 * that is no longer true cannot survive that either: the shell reports every
 * transition, and keeps reporting while anybody is at the machine.
 *
 * WIRED, at last. `PresenceRuntime` calls it when the viewer id changes and
 * when it unmounts - a sign-out, a session swap, a shell that reloads the
 * workspace as somebody else. Until this round the function existed with no
 * production caller at all and a docblock describing a gesture that was never
 * made; the evidence of one user simply became the evidence of the next.
 */
export function resetSystemActivity(): void {
  windowVisible = true;
  /* `measuring` is NOT cleared: it describes this DOCUMENT, not the person in
     front of it, and `PresenceRuntime` is still mounted. */
  locked = false;
  lastState = null;
  lastClaim = null;
  presenceActions.forgetIdleEvidence();
  getRealtime().resetIdleEvidence();
  /* Through the notifier, not around it: a reset that changed the answer
     without telling the reporter would leave the socket asserting the old
     one. */
  for (const handler of evidenceListeners) handler();
}

/**
 * WHAT THIS CLIENT MAY TELL THE SERVER ABOUT ITS USER, RIGHT NOW.
 *
 * THREE facts, because the gateway treats them as three and conflating any two
 * of them has cost this feature a round each:
 *
 *   `background` — this window is not on screen. A SCHEDULING fact. It buys the
 *     extended heartbeat grace and refreshes the presence TTL and it must never
 *     read as Away, because Teams does not make you Away for minimising.
 *   `idle` — nobody is at the machine: the screen is locked, or the evidence is
 *     older than this user's own auto-away threshold. A PRESENCE fact.
 *   `measured` — this document watches its user AT ALL (see `measuring`). An
 *     EPISTEMIC fact, and the one that was missing: without it "I can see they
 *     are here" and "I have never looked" are the same value, `idle: false`,
 *     and the second was being sent as `active`.
 *   `observed` — this document has ACTUALLY TAKEN A READING. The second half of
 *     the same epistemic fact, and the one that was missing after it: a
 *     document that watches its user has still SEEN nothing between opening and
 *     its first event, and `idle: false` was again the value for both "measured,
 *     somebody is here" and "guessing, from the page load". `framesFor` promoted
 *     that guess to `active` — the only frame that clears F_IDLE, and one that
 *     does not age. The guess may ride the `ping`, which expires on its own at
 *     the user's threshold; it may not latch a flag.
 *
 * They are genuinely independent - a locked machine with the app minimised is
 * both - and the answer to each is somebody else's: `windowVisible` is the
 * shell's, `selfIsIdle` is the presence store's, which is the same number this
 * user's own chip is drawn from, and `measuring` is this document's. Nothing is
 * decided here; the three are simply read in one place so that every frame
 * agrees with every other frame and with the chip.
 */
export interface PresenceClaim {
  readonly background: boolean;
  readonly idle: boolean;
  readonly measured: boolean;
  readonly observed: boolean;
}

function documentIsHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState !== 'visible';
}

export function presenceClaim(now: number = Date.now()): PresenceClaim {
  return {
    background: !windowVisible || documentIsHidden(),
    idle: measuring && selfIsIdle(now),
    measured: measuring,
    observed: selfHasEvidence(),
  };
}

/**
 * The frames that carry a claim, in the order the gateway must receive them.
 *
 * `apps/gateway/src/core/dispatch.ts` gives each of the three a DIFFERENT and
 * partial effect, so the mapping is not a formality:
 *
 *   active      setBackground(false) AND applyActivity(true) - the only frame
 *               that can clear F_IDLE.
 *   background  setBackground(true). Touches presence not at all.
 *   idle        applyActivity(false). Touches the background flag not at all.
 *
 * Hence: a present user in a minimised window needs `active` to clear F_IDLE
 * and then `background` to buy back the grace `active` just took away - one
 * extra frame on a transition nobody makes twice a minute, against a colleague
 * seeing Away for up to a ping interval. An absent user in a minimised window
 * needs no `active` at all, because F_IDLE is what is being set.
 */
function framesFor(claim: PresenceClaim): readonly ('active' | 'idle' | 'background')[] {
  /*
   * A DOCUMENT THAT HAS NOT LOOKED SAYS NOTHING ABOUT A HUMAN.
   *
   * `idle: false` arrives here from two unrelated places - "the evidence says
   * somebody is at this machine" and "there is no evidence, because this
   * document measures nothing" - and the second used to be sent as `active`,
   * the strongest claim in the protocol and the only frame that clears F_IDLE.
   *
   * A desktop CALL WINDOW is that document: `AppProviders` mounts
   * `RealtimeRuntime` in it, `AppOverlays` deliberately does not mount
   * `PresenceRuntime`, so it has no DOM listener, no OS report and no tick. It
   * was asserting a keyboard on mount and again on every reconnect. Because the
   * gateway resolves `activeCount > 0 ? available : away` across a user's
   * sockets, that one connection answered for all of them: lock the
   * workstation, and the workspace's correct `idle` was outvoted by a window
   * nobody was typing into. It is the same fault as the lock defect - an
   * ASSUMPTION written where only an OBSERVATION belongs - one layer further
   * out, and it survived the round that fixed the first one.
   *
   * Its WINDOW is still its own business. `background` is a fact about a window
   * and it buys the heartbeat grace that keeps the socket alive. It is one-way
   * for such a document, because `active` is the only frame that clears the
   * flag and this document may not send one; being left on the GENTLER reap
   * branch is the safe direction to be stuck in, and the branch is unreachable
   * anyway while its own 25 s ping keeps arriving.
   */
  if (!claim.measured) return claim.background ? ['background'] : [];
  /*
   * A GUESS MAY AGE OUT. IT MAY NOT LATCH.
   *
   * A document that watches its user has still seen nothing at all between
   * opening and its first event, and `idle: false` is what that looks like -
   * `selfIsIdle` falls back to the load-time assumption. Sending it as `active`
   * turned the guess into the strongest claim in the protocol AND the only one
   * that clears F_IDLE, on a flag that does not expire; executed, a workspace
   * reloaded into a LOCKED session reported a human at the keyboard until the
   * shell's first report arrived to take it back.
   *
   * The difference between the two carriers is the whole rule. `ping.idleMs`
   * carries the same guess as a NUMBER that grows, so the gateway retires it at
   * this user's own threshold with no further help from anybody; `active` is a
   * latch, and nothing but another frame can undo a latch. So an unobserved
   * document says nothing where it would have said `active`, and the arrival
   * `hincrby(k, "a", 1)` it leaves standing is retired by its own ping on
   * exactly the same schedule the guess deserves.
   *
   * IT MAY STILL SAY `idle`, and the asymmetry is deliberate and is the same
   * one every rule in this file has: claiming ABSENCE from thin evidence costs
   * a green dot; claiming PRESENCE from it is the failure the whole feature
   * exists to remove. It is also what keeps the browser path exactly as it was
   * - a tab nobody has touched for five minutes still sends `idle` on the beat,
   * from the seed, because by then the seed says the honest thing.
   */
  if (!claim.observed && !claim.idle) return claim.background ? ['background'] : [];
  if (!claim.idle) return claim.background ? ['active', 'background'] : ['active'];
  return claim.background ? ['background', 'idle'] : ['idle'];
}

let lastClaim: PresenceClaim | null = null;

/**
 * WOULD THIS SAY ANYTHING NEW? Compared by what would be SENT, not by fields.
 *
 * The claim has four facts and the protocol has three frames, so the mapping is
 * many-to-one and a field-wise comparison answers the wrong question twice
 * over. It sent a redundant frame whenever a fact moved without changing the
 * answer - an unobserved document taking its first reading of a machine that
 * was already idle repeated `idle` at the gateway, which drops it in
 * `applyActivity` anyway. And it silently rotted the other way: a mutation run
 * deleted the `measured` comparison from this function and every suite in the
 * repo stayed green, because `lastClaim` was being reached around elsewhere.
 *
 * Comparing `framesFor` makes both impossible by construction. Every fact that
 * can change what the server is told is compared exactly because it changes it,
 * and no fact that cannot is compared at all. The transition that MUST still be
 * announced is the one this round added - a document that has measured nothing
 * says nothing, and the moment it measures a human it owes the gateway an
 * `active` - and `[] !== ['active']`, so it is.
 */
const sameClaim = (a: PresenceClaim, b: PresenceClaim): boolean =>
  framesFor(a).join(',') === framesFor(b).join(',');

/**
 * TELL THE SERVER. The ONE place a `presence` frame is sent.
 *
 * ON CHANGE ONLY, unless forced. The shell keeps this document supplied every
 * 10 s while somebody is at the machine, and a `presence` frame every 10 s is a
 * second heartbeat nobody asked for; in between, the claim rides out on the
 * `ping` that was going to be sent anyway, carrying the same evidence as a
 * number.
 *
 * FORCED ON A NEW CONNECTION, and that is not an optimisation. A hint lives on
 * the CONNECTION that received it, so every reconnect starts foreground and
 * un-idle at the server - and reconnects are not rare (a token refresh, a
 * network blip, a gateway deploy reconnects every client at once). Sending
 * nothing because "the claim has not changed" is precisely how a locked
 * workstation came back green at 03:00.
 */
export function reportPresence(force = false): PresenceClaim {
  const claim = presenceClaim();
  if (force) lastClaim = null;
  if (lastClaim !== null && sameClaim(lastClaim, claim)) return claim;
  lastClaim = claim;
  const realtime = getRealtime();
  for (const frame of framesFor(claim)) realtime.activity(frame);
  return claim;
}

/**
 * ONE REPORT, APPLIED.
 *
 * Ordering is deliberate and it is the whole of the repair. EVERY piece of
 * evidence is written first - the measurement into both clocks, then the lock
 * into both clocks - and only then is anybody told that something changed. It
 * used to run the other way round: `setWindowVisible` fired its listeners on
 * line one, `RealtimeRuntime.report()` ran inside that call and sent
 * `activity('active')` from the evidence of the PREVIOUS report, and the honest
 * instant was written two lines later where the forward-only clocks could no
 * longer accept it. A window restored on a machine nobody had touched for forty
 * minutes reported a human at the keyboard. The docblock here claimed the
 * opposite ordering; it does now.
 */
export function applySystemActivity(report: DesktopSystemActivity): void {
  const before = evidence();

  /*
   * THE MOMENT A HUMAN WAS LAST VOUCHED FOR, dated from the SHELL'S clock and
   * not from ours. `report.at` is when the reading was taken; subtracting the
   * idle time from it names one absolute instant, so a report the shell's bus
   * held back across a reload is correctly old rather than looking like news.
   * Rounding it up to the present would be inventing evidence either way.
   *
   * `report.idleMs` is already floored at the time the shell was not running
   * (shared/systemIdle.ts), so a machine that slept for an hour arrives as an
   * hour of idle whatever its tick counter claims. That floor is now the ONLY
   * thing that decides what is said about a resume: the frame used to be picked
   * from `report.state`, which comes raw from `getSystemIdleState()` and is
   * derived from the same under-reporting counter the floor exists to correct -
   * so on the platforms the floor was written for, the floor was applied to the
   * number and then cancelled by the frame.
   */
  const at = report.at - Math.max(0, report.idleMs);
  presenceActions.noteActivityAt(at);
  getRealtime().noteActivity(at);

  /*
   * LOCKED IS NOT IDLE, AND `unknown` IS NOT UNLOCKED.
   *
   * Teams puts you Away the moment the workstation locks, without waiting out
   * the idle timer, because locking is an unambiguous statement that you have
   * left where five quiet minutes is only a guess. It is held as a flag rather
   * than folded into a clock because winding a clock back would be undone by
   * the next thing that writes it - and because the measurement at that instant
   * is genuinely "a human was here a second ago": Win+L is input.
   *
   * `unknown` is what `getSystemIdleState()` answers when it throws or cannot
   * tell. It is the ABSENCE of an observation, and it used to be read as one:
   * a single `unknown` sample cleared the lock and stamped `lastInputAt` at
   * now, so this user's own chip and tray icon went Available with the screen
   * still black. Nothing but a definite reading may take a lock back, and the
   * shell has two ways to give it one - a non-locked idle state, or the
   * `unlock-screen` event, which it now records so that a platform whose idle
   * STATE cannot say `locked` still can (main/systemPresence.ts).
   *
   * A SLEEPING MACHINE needs no branch of its own: the floor above turns an
   * hour asleep into an hour of idle and the ordinary threshold does the rest.
   */
  if (report.state !== 'unknown') locked = report.state === 'locked';
  presenceActions.setSystemLocked(locked);
  getRealtime().setAbsent(locked);

  windowVisible = report.windowVisible;
  lastState = report.state;

  /*
   * IS THERE ANYTHING TO SAY? Three ways there can be, and the third one is
   * easy to leave out and impossible to see when it is missing.
   *
   *   A RESUME is always news, even when nothing else moved: the shell was not
   *     running, so this socket may have been reaped and re-established, and
   *     this document's idea of what the server believes is worth nothing.
   *   THE EVIDENCE moved - locked, unlocked, minimised, restored, went quiet.
   *   THE CLAIM moved while the evidence did not. The evidence is coarse: the
   *     shell's `active`/`idle` flip is at 60 s and the away threshold is at
   *     300 s, so the measurement can cross the threshold in EITHER direction
   *     with the state unchanged. It happens on exactly the path the resume
   *     floor exists for - a platform that under-reports wakes at `active` with
   *     an hour floored onto it, and the honest `active` two seconds later is
   *     the same state with a different answer.
   *
   * `tick()` goes with it, so the person's OWN chip and the frame their
   * colleagues see are recomputed from one reading at one instant. Leaving the
   * store to its own 15 s beat is how the two ends of this feature drift apart,
   * which is the whole subject of this file.
   */
  const claim = presenceClaim();
  const news =
    report.resumedAfterMs > 0 ||
    evidence() !== before ||
    (lastClaim !== null && !sameClaim(lastClaim, claim));
  if (!news) return;
  if (report.resumedAfterMs > 0) lastClaim = null;

  presenceActions.tick();
  for (const handler of evidenceListeners) handler();
}
